/**
 * IndexedDB wrapper for storing ScheduleManifest and asset metadata.
 * Provides 24h TTL for manifests and CRUD operations for asset metadata.
 */

import type { ScheduleManifest } from '@dooh/shared';
import {
  IDB_NAME,
  IDB_VERSION,
  IDB_STORES,
  MANIFEST_TTL_MS,
  type CachedAssetMeta,
} from './types';

/** A concrete manifest record with a computed isExpired property */
interface ManifestRecordData {
  version: string;
  manifestJson: string;
  storedAt: number;
  screenId: string;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(IDB_NAME, IDB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      // Manifests store — keyed by screenId
      if (!db.objectStoreNames.contains(IDB_STORES.manifests)) {
        const manifestStore = db.createObjectStore(IDB_STORES.manifests, {
          keyPath: 'screenId',
        });
        manifestStore.createIndex('version', 'version', { unique: false });
      }

      // Asset metadata store — keyed by assetId
      if (!db.objectStoreNames.contains(IDB_STORES.assetMeta)) {
        const assetStore = db.createObjectStore(IDB_STORES.assetMeta, {
          keyPath: 'assetId',
        });
        assetStore.createIndex('lastPlayedAt', 'lastPlayedAt', { unique: false });
        assetStore.createIndex('isFallback', 'isFallback', { unique: false });
        assetStore.createIndex('cachedAt', 'cachedAt', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function withTransaction<T>(
  storeName: string,
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDB().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(storeName, mode);
        const store = tx.objectStore(storeName);
        const request = fn(store);

        // Capture the result from the request success event, but DON'T
        // resolve the promise yet — wait for the transaction to commit (#51).
        // Resolving on request.onsuccess means the caller acts on data
        // before the transaction commits; if the tx aborts (quota exceeded
        // on a Pi), the caller falsely believes the write succeeded.
        let result: T;

        request.onsuccess = () => {
          result = request.result;
        };
        request.onerror = () => {
          db.close();
          reject(request.error);
        };

        // Resolve only after the transaction commits (#51)
        tx.oncomplete = () => {
          db.close();
          resolve(result);
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
        tx.onabort = () => {
          db.close();
          reject(tx.error ?? new Error('IndexedDB transaction aborted'));
        };
      }),
  );
}

// ─── Manifest Operations ─────────────────────────────────────────────

/**
 * Store a schedule manifest in IndexedDB.
 */
export async function storeManifest(
  manifest: ScheduleManifest,
  screenId: string,
): Promise<void> {
  const record: ManifestRecordData = {
    version: manifest.version,
    manifestJson: JSON.stringify(manifest),
    storedAt: Date.now(),
    screenId,
  };

  await withTransaction(IDB_STORES.manifests, 'readwrite', (store) =>
    store.put(record),
  );
}

/**
 * Load a manifest from IndexedDB. Returns null if not found or expired.
 */
export async function loadManifest(
  screenId: string,
): Promise<{ manifest: ScheduleManifest; isExpired: boolean } | null> {
  const record = await withTransaction<ManifestRecordData | undefined>(
    IDB_STORES.manifests,
    'readonly',
    (store) => store.get(screenId),
  );

  if (!record) return null;

  const isExpired = Date.now() - record.storedAt > MANIFEST_TTL_MS;
  const manifest: ScheduleManifest = JSON.parse(record.manifestJson);

  return { manifest, isExpired };
}

/**
 * Delete a manifest from IndexedDB.
 */
export async function deleteManifest(screenId: string): Promise<void> {
  await withTransaction(IDB_STORES.manifests, 'readwrite', (store) =>
    store.delete(screenId),
  );
}

// ─── Asset Metadata Operations ───────────────────────────────────────

/**
 * Store metadata for a cached asset.
 */
export async function storeAssetMeta(meta: CachedAssetMeta): Promise<void> {
  await withTransaction(IDB_STORES.assetMeta, 'readwrite', (store) =>
    store.put(meta),
  );
}

/**
 * Load metadata for a cached asset.
 */
export async function loadAssetMeta(
  assetId: string,
): Promise<CachedAssetMeta | null> {
  const result = await withTransaction<CachedAssetMeta | undefined>(
    IDB_STORES.assetMeta,
    'readonly',
    (store) => store.get(assetId),
  );
  return result ?? null;
}

/**
 * Load all cached asset metadata.
 */
export async function loadAllAssetMeta(): Promise<CachedAssetMeta[]> {
  const result = await withTransaction<CachedAssetMeta[]>(
    IDB_STORES.assetMeta,
    'readonly',
    (store) => store.getAll(),
  );
  return result ?? [];
}

/**
 * Update the last-played timestamp for an asset (LRU tracking).
 */
export async function touchAssetLastPlayed(assetId: string): Promise<void> {
  const meta = await loadAssetMeta(assetId);
  if (meta) {
    meta.lastPlayedAt = Date.now();
    await storeAssetMeta(meta);
  }
}

/**
 * Delete asset metadata.
 */
export async function deleteAssetMeta(assetId: string): Promise<void> {
  await withTransaction(IDB_STORES.assetMeta, 'readwrite', (store) =>
    store.delete(assetId),
  );
}

/**
 * Delete all asset metadata entries.
 */
export async function clearAllAssetMeta(): Promise<void> {
  await withTransaction(IDB_STORES.assetMeta, 'readwrite', (store) =>
    store.clear(),
  );
}
